import React, { useEffect, useState, useCallback } from 'react';
import '../super-admin.css';
import { Typography, Space, Button, Select, Card, Table, Tag, message, Popconfirm, Row, Col } from 'antd';
import { DownloadOutlined, DatabaseOutlined, SafetyOutlined } from '@ant-design/icons';
import { API_BASE as apiBase, authHeaders } from '../../config/api';

const { Title } = Typography;

const formatBytes = (b) => {
  if (!b && b !== 0) return '';
  const units = ['B','KB','MB','GB'];
  let i = 0; let v = b;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(1)} ${units[i]}`;
};

const DBBackupRestore = () => {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);

  // Load backups from API
  const loadBackups = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/admin/backups`, {
        headers: { ...authHeaders() }
      });
      if (!res.ok) throw new Error('Failed to load backups');
      const data = await res.json();
      setBackups(data);
    } catch (err) {
      message.error(err.message || 'Error loading backups');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBackups();
  }, [loadBackups]);

  // Create a new backup
  const backupNow = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch(`${apiBase}/admin/backups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() }
      });
      if (!res.ok) throw new Error('Failed to create backup');
      const newBackup = await res.json();
      setBackups(prev => [newBackup, ...prev]);
      message.success('Database backup created successfully!');
    } catch (err) {
      message.error(err.message || 'Error creating backup');
    } finally {
      setBusy(false);
    }
  };

  // Restore from selected backup
  const restore = async () => {
    if (!selected) { 
      message.warning('Please select a backup to restore.'); 
      return; 
    }
    setBusy(true);
    try {
      const res = await fetch(`${apiBase}/admin/backups/${selected}/restore`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() }
      });
      if (!res.ok) throw new Error('Failed to restore backup');
      const result = await res.json();
      message.success(result.message || 'Database restored successfully!');
    } catch (err) {
      message.error(err.message || 'Error restoring backup');
    } finally {
      setBusy(false);
    }
  };

  // Download backup file
  const downloadBackup = async (id) => {
    try {
      const res = await fetch(`${apiBase}/admin/backups/${id}/download`, {
        headers: { ...authHeaders() }
      });
      if (!res.ok) throw new Error('Failed to download backup');
      
      // Create blob and download
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${id}.sql`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      message.success('Backup download started');
    } catch (err) {
      message.error(err.message || 'Error downloading backup');
    }
  };

  // Delete backup
  const remove = async (id) => {
    try {
      const res = await fetch(`${apiBase}/admin/backups/${id}`, {
        method: 'DELETE',
        headers: { ...authHeaders() }
      });
      if (!res.ok) throw new Error('Failed to delete backup');
      
      setBackups(prev => prev.filter(b => b.id !== id));
      if (selected === id) setSelected(null);
      message.success('Backup deleted successfully');
    } catch (err) {
      message.error(err.message || 'Error deleting backup');
    }
  };

  return (
    <div className="sa-content">
      <Title level={2} style={{ marginBottom: 24 }}>Database Backup & Restore</Title>

      <Row gutter={[16, 16]}>
        {/* Backup Management Card */}
        <Col xs={24} lg={14}>
          <Card 
            hoverable 
            style={{ borderRadius: 14 }} 
            bodyStyle={{ padding: 20 }}
          >
            {/* Header with database icon */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ 
                  width: 44, 
                  height: 44, 
                  borderRadius: 10, 
                  background: '#05966922', 
                  color: '#059669', 
                  display: 'grid', 
                  placeItems: 'center', 
                  fontSize: 20 
                }}>
                  <DatabaseOutlined />
                </div>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 600, color: '#1f2937' }}>Backup Management</div>
                  <div style={{ color: '#64748b', fontSize: 13 }}>Create and manage database backups</div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ 
              display: 'flex', 
              gap: 8, 
              flexWrap: 'wrap',
              padding: '12px 16px', 
              background: '#f0f9ff', 
              borderRadius: 8, 
              marginBottom: 16 
            }}>
              <Button type="primary" onClick={backupNow} loading={busy} style={{ flex: 1, minWidth: 'auto' }}>
                Create Backup
              </Button>
              <Button onClick={loadBackups} disabled={loading || busy}>
                Refresh List
              </Button>
            </div>

            {/* Quick Select */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#1f2937', marginBottom: 8 }}>
                Quick Select Backup
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <Select
                  style={{ minWidth: 280, flex: 1 }}
                  placeholder="Choose a backup to select"
                  value={selected}
                  onChange={setSelected}
                  options={backups.map(b => ({
                    value: b.id,
                    label: `${b.id} — ${new Date(b.created_at).toLocaleString()} — ${formatBytes(b.size)}`,
                  }))}
                  allowClear
                  loading={loading}
                />
                {selected && <Tag color="blue">Selected: {selected}</Tag>}
              </div>
            </div>

            {/* Backups Table */}
            <Table
              rowKey="id"
              dataSource={backups}
              loading={loading}
              pagination={{ pageSize: 8, size: 'small' }}
              size="small"
              columns={[
                { 
                  title: 'Backup ID', 
                  dataIndex: 'id', 
                  key: 'id', 
                  width: 180,
                  render: (id) => <code style={{ fontSize: '11px' }}>{id}</code>
                },
                { 
                  title: 'Created', 
                  dataIndex: 'created_at', 
                  key: 'created_at', 
                  width: 140, 
                  render: (t) => new Date(t).toLocaleString('en-US', { 
                    month: 'short', 
                    day: 'numeric', 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })
                },
                { 
                  title: 'Size', 
                  dataIndex: 'size', 
                  key: 'size', 
                  width: 80, 
                  render: (s) => formatBytes(s) 
                },
                { 
                  title: 'Actions', 
                  key: 'actions', 
                  width: 180, 
                  render: (_, backup) => (
                    <Space size="small">
                      <Button 
                        size="small" 
                        onClick={() => setSelected(backup.id)} 
                        disabled={busy}
                        type={selected === backup.id ? 'primary' : 'default'}
                      >
                        {selected === backup.id ? '✓' : 'Select'}
                      </Button>
                      <Button 
                        size="small" 
                        icon={<DownloadOutlined />}
                        onClick={() => downloadBackup(backup.id)}
                        disabled={busy}
                        title="Download"
                      />
                      <Popconfirm
                        title="Delete backup?"
                        description="This action cannot be undone."
                        onConfirm={() => remove(backup.id)}
                        okText="Delete"
                        cancelText="Cancel"
                        okType="danger"
                      >
                        <Button size="small" danger disabled={busy}>
                          ×
                        </Button>
                      </Popconfirm>
                    </Space>
                  ) 
                },
              ]}
            />
          </Card>
        </Col>

        {/* Restore & Safety Card */}
        <Col xs={24} lg={10}>
          <Card 
            hoverable 
            style={{ borderRadius: 14 }} 
            bodyStyle={{ padding: 20 }}
          >
            {/* Header with safety icon */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ 
                  width: 44, 
                  height: 44, 
                  borderRadius: 10, 
                  background: '#dc262622', 
                  color: '#dc2626', 
                  display: 'grid', 
                  placeItems: 'center', 
                  fontSize: 20 
                }}>
                  <SafetyOutlined />
                </div>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 600, color: '#1f2937' }}>Database Restore</div>
                  <div style={{ color: '#64748b', fontSize: 13 }}>Restore from backup</div>
                </div>
              </div>
            </div>

            {/* Warning Notice */}
            <div style={{ 
              padding: '12px 16px', 
              background: '#fef2f2', 
              borderRadius: 8, 
              marginBottom: 16,
              border: '1px solid #fecaca'
            }}>
              <div style={{ fontSize: 13, color: '#991b1b', marginBottom: 4 }}>
                <strong>⚠️ Critical Warning</strong>
              </div>
              <div style={{ fontSize: 12, color: '#7f1d1d', lineHeight: 1.4 }}>
                Database restoration will <strong>permanently overwrite</strong> all current data. 
                This action cannot be undone. Always create a backup before restoring.
              </div>
            </div>

            {/* Selected Backup Info */}
            {selected ? (
              <div style={{ 
                padding: '12px 16px', 
                background: '#eff6ff', 
                borderRadius: 8, 
                marginBottom: 16,
                border: '1px solid #dbeafe'
              }}>
                <div style={{ fontSize: 13, color: '#1e40af', marginBottom: 4 }}>
                  <strong>Selected Backup</strong>
                </div>
                <div style={{ fontSize: 12, color: '#1e3a8a' }}>
                  <div><strong>ID:</strong> {selected}</div>
                  {backups.find(b => b.id === selected) && (
                    <>
                      <div><strong>Created:</strong> {new Date(backups.find(b => b.id === selected).created_at).toLocaleString()}</div>
                      <div><strong>Size:</strong> {formatBytes(backups.find(b => b.id === selected).size)}</div>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div style={{ 
                padding: '12px 16px', 
                background: '#f9fafb', 
                borderRadius: 8, 
                marginBottom: 16,
                textAlign: 'center'
              }}>
                <div style={{ fontSize: 13, color: '#6b7280', fontStyle: 'italic' }}>
                  No backup selected for restoration
                </div>
              </div>
            )}

            {/* Restore Button */}
            <Popconfirm
              title="Restore Database"
              description={
                <div>
                  <div>This will <strong>overwrite all current data</strong>.</div>
                  <div>Are you absolutely sure?</div>
                </div>
              }
              onConfirm={restore}
              okText="Yes, Restore"
              cancelText="Cancel"
              okType="danger"
              disabled={!selected || busy}
            >
              <Button 
                danger 
                disabled={!selected || busy}
                loading={busy}
                style={{ width: '100%' }}
                size="large"
              >
                {busy ? 'Restoring...' : 'Restore Selected Backup'}
              </Button>
            </Popconfirm>

            {/* Backup Statistics */}
            <div style={{ 
              marginTop: 16,
              padding: '12px 16px', 
              background: '#f8fafc', 
              borderRadius: 8 
            }}>
              <div style={{ fontSize: 13, color: '#374151', marginBottom: 8 }}>
                <strong>Backup Statistics</strong>
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', display: 'flex', justifyContent: 'space-between' }}>
                <span>Total Backups:</span>
                <span><strong>{backups.length}</strong></span>
              </div>
              {backups.length > 0 && (
                <>
                  <div style={{ fontSize: 12, color: '#6b7280', display: 'flex', justifyContent: 'space-between' }}>
                    <span>Total Size:</span>
                    <span><strong>{formatBytes(backups.reduce((sum, b) => sum + b.size, 0))}</strong></span>
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280', display: 'flex', justifyContent: 'space-between' }}>
                    <span>Latest:</span>
                    <span><strong>{new Date(Math.max(...backups.map(b => new Date(b.created_at)))).toLocaleDateString()}</strong></span>
                  </div>
                </>
              )}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DBBackupRestore;
